import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, TriangleAlert, CalendarClock, Home, ArrowUpRight, Building2, FileUp, Sparkles, Loader2 } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");
const fmtMoney = (n) => (n ? "$" + Number(n).toLocaleString() : "—");

function Stat({ label, value, icon: Icon, tone }) {
  return (
    <div className="bg-card border border-border rounded-md p-6 flex items-start justify-between" data-testid={`stat-${label}`}>
      <div>
        <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground font-semibold">{label}</p>
        <p className="text-4xl font-heading text-primary mt-2">{value}</p>
      </div>
      <div className={`w-10 h-10 rounded-md grid place-items-center ${tone}`}><Icon className="w-5 h-5" /></div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", address: "", price: "", acceptance_date: "", buyer_agent_email: "", listing_coordinator_email: "" });
  const [importOpen, setImportOpen] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [review, setReview] = useState(null);
  const [filter, setFilter] = useState("active");

  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/deals/stats").then((r) => r.data) });
  const { data: deals = [], isLoading } = useQuery({ queryKey: ["deals"], queryFn: () => api.get("/deals").then((r) => r.data) });

  const create = useMutation({
    mutationFn: (payload) => api.post("/deals", payload).then((r) => r.data),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setOpen(false);
      toast.success("Transaction created");
      navigate(`/deals/${d.id}`);
    },
    onError: (e) => toast.error(formatError(e.response?.data?.detail)),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.title || !form.acceptance_date) return toast.error("Title and acceptance date are required");
    create.mutate({ ...form, price: form.price ? Number(form.price) : null });
  };

  const onPickPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") return toast.error("Please upload a PDF file");
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/deals/parse-contract", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      const c = data.contingencies || {};
      const low = new Set(Array.isArray(data.low_confidence) ? data.low_confidence : []);
      const isEmpty = (v) => v === "" || v === null || v === undefined;
      const flag = (srcKey, val) => low.has(srcKey) || isEmpty(val);
      setReview({
        title: data.property_address || "",
        address: data.property_address || "",
        price: data.purchase_price ?? "",
        acceptance_date: data.acceptance_date || "",
        buyer_names: data.buyer_names || "",
        seller_names: data.seller_names || "",
        buyer_agent_email: "",
        listing_coordinator_email: "",
        contract_file_id: data.contract_file_id || null,
        contract_filename: data.contract_filename || null,
        loan: c.loan ?? "",
        appraisal: c.appraisal ?? "",
        investigation: c.investigation ?? "",
        close_of_escrow: c.close_of_escrow ?? "",
        flags: {
          title: flag("property_address", data.property_address),
          address: flag("property_address", data.property_address),
          price: flag("purchase_price", data.purchase_price),
          acceptance_date: flag("acceptance_date", data.acceptance_date),
          buyer_names: flag("buyer_names", data.buyer_names),
          seller_names: flag("seller_names", data.seller_names),
          loan: flag("loan", c.loan),
          appraisal: flag("appraisal", c.appraisal),
          investigation: flag("investigation", c.investigation),
          close_of_escrow: flag("close_of_escrow", c.close_of_escrow),
        },
      });
      setImportOpen(false);
      toast.success("Contract read — please review the details");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not read that contract");
    } finally {
      setParsing(false);
    }
  };

  const createFromReview = () => {
    if (!review.title || !review.acceptance_date) return toast.error("Title and acceptance date are required");
    const contingencies = {};
    ["loan", "appraisal", "investigation", "close_of_escrow"].forEach((k) => {
      if (review[k] !== "" && review[k] !== null && review[k] !== undefined) contingencies[k] = Number(review[k]);
    });
    create.mutate({
      title: review.title,
      address: review.address,
      price: review.price ? Number(review.price) : null,
      acceptance_date: review.acceptance_date,
      buyer_agent_email: review.buyer_agent_email || null,
      listing_coordinator_email: review.listing_coordinator_email || null,
      buyer_names: review.buyer_names || null,
      seller_names: review.seller_names || null,
      contract_file_id: review.contract_file_id || null,
      contract_filename: review.contract_filename || null,
      contingencies,
    });
    setReview(null);
  };

  const flagged = (k) => !!(review && review.flags && review.flags[k]);
  const ringCls = (k) => (flagged(k) ? "border-amber-400 focus-visible:ring-amber-400 bg-amber-50/50" : "");
  const setField = (k, v) => setReview({ ...review, [k]: v, flags: { ...(review.flags || {}), [k]: false } });
  const VerifyBadge = ({ k }) => flagged(k)
    ? <span data-testid={`review-verify-${k}`} className="ml-1.5 text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full align-middle">verify</span>
    : null;

  const STATUS_ORDER = { active: 0, closed: 1, cancelled: 2 };
  const visible = [...deals]
    .filter((d) => (filter === "all" ? true : (d.effective_status || "active") === filter))
    .sort((a, b) => (STATUS_ORDER[a.effective_status] ?? 0) - (STATUS_ORDER[b.effective_status] ?? 0));

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-accent font-semibold mb-1">Welcome back</p>
            <h1 className="text-4xl sm:text-5xl text-foreground">{user?.name || "Agent"}<span className="italic text-primary">'s</span> workflows</h1>
          </div>
          <div className="flex items-center gap-2">
          <Dialog open={importOpen} onOpenChange={setImportOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-contract-button" className="rounded-full gap-2 border-primary text-primary hover:bg-primary/5">
                <Sparkles className="w-4 h-4" /> Import from contract
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card">
              <DialogHeader><DialogTitle className="font-heading text-2xl text-primary">Auto-fill from RPA contract</DialogTitle></DialogHeader>
              <DialogDescription className="text-sm text-muted-foreground">Upload the signed California Residential Purchase Agreement (PDF). We'll read the property, price, parties, acceptance date and contingency deadlines, then let you review before creating the transaction.</DialogDescription>
              <label data-testid="contract-dropzone" className={`mt-2 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/60 transition-colors ${parsing ? "opacity-60 pointer-events-none" : ""}`}>
                {parsing ? <Loader2 className="w-7 h-7 text-primary animate-spin" /> : <FileUp className="w-7 h-7 text-primary" />}
                <span className="text-sm font-medium text-foreground">{parsing ? "Reading the contract…" : "Click to choose a PDF"}</span>
                <span className="text-xs text-muted-foreground">CAR RPA · PDF only</span>
                <input type="file" accept="application/pdf" className="hidden" data-testid="contract-file-input" onChange={onPickPdf} disabled={parsing} />
              </label>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-deal-button" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full gap-2">
                <Plus className="w-4 h-4" /> New transaction
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card">
              <DialogHeader><DialogTitle className="font-heading text-2xl text-primary">New transaction</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="new-deal-form">
                <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Title *</Label>
                  <Input data-testid="deal-title-input" className="mt-1.5" placeholder="2728 Somerset Park" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Property address</Label>
                  <Input data-testid="deal-address-input" className="mt-1.5" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Price</Label>
                    <Input data-testid="deal-price-input" type="number" className="mt-1.5" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
                  <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Acceptance date *</Label>
                    <Input data-testid="deal-date-input" type="date" className="mt-1.5" value={form.acceptance_date} onChange={(e) => setForm({ ...form, acceptance_date: e.target.value })} /></div>
                </div>
                <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Buyer agent email (CC)</Label>
                  <Input data-testid="deal-buyer-input" type="email" className="mt-1.5" value={form.buyer_agent_email} onChange={(e) => setForm({ ...form, buyer_agent_email: e.target.value })} /></div>
                <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Your coordinator email (CC)</Label>
                  <Input data-testid="deal-coord-input" type="email" className="mt-1.5" value={form.listing_coordinator_email} onChange={(e) => setForm({ ...form, listing_coordinator_email: e.target.value })} /></div>
                <DialogFooter>
                  <Button type="submit" disabled={create.isPending} data-testid="deal-submit-button" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded">
                    {create.isPending ? "Creating…" : "Create transaction"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>

          <Dialog open={!!review} onOpenChange={(o) => { if (!o) setReview(null); }}>
            <DialogContent className="bg-card max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-2xl text-primary flex items-center gap-2"><Sparkles className="w-5 h-5" /> Review parsed contract</DialogTitle></DialogHeader>
              <DialogDescription className="text-sm text-muted-foreground">We extracted the following from your contract. Edit anything that looks off, then create the transaction.</DialogDescription>
              {review && (
                <div className="space-y-4" data-testid="contract-review-form">
                  {Object.values(review.flags || {}).some(Boolean) && (
                    <div data-testid="review-verify-banner" className="rounded-md border border-amber-300/70 bg-amber-50/70 px-3 py-2.5 flex items-start gap-2">
                      <TriangleAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-900 leading-relaxed">A few fields were unclear or missing in the contract. Please double-check anything marked <span className="font-semibold">verify</span> — especially price and dates — before creating.</p>
                    </div>
                  )}
                  <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Title *<VerifyBadge k="title" /></Label>
                    <Input data-testid="review-title-input" className={`mt-1.5 ${ringCls("title")}`} value={review.title} onChange={(e) => setField("title", e.target.value)} /></div>
                  <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Property address<VerifyBadge k="address" /></Label>
                    <Input data-testid="review-address-input" className={`mt-1.5 ${ringCls("address")}`} value={review.address} onChange={(e) => setField("address", e.target.value)} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Price<VerifyBadge k="price" /></Label>
                      <Input data-testid="review-price-input" type="number" className={`mt-1.5 ${ringCls("price")}`} value={review.price} onChange={(e) => setField("price", e.target.value)} /></div>
                    <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Acceptance date *<VerifyBadge k="acceptance_date" /></Label>
                      <Input data-testid="review-date-input" type="date" className={`mt-1.5 ${ringCls("acceptance_date")}`} value={review.acceptance_date} onChange={(e) => setField("acceptance_date", e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Buyer(s)<VerifyBadge k="buyer_names" /></Label>
                      <Input data-testid="review-buyer-names-input" className={`mt-1.5 ${ringCls("buyer_names")}`} value={review.buyer_names} onChange={(e) => setField("buyer_names", e.target.value)} /></div>
                    <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Seller(s)<VerifyBadge k="seller_names" /></Label>
                      <Input data-testid="review-seller-names-input" className={`mt-1.5 ${ringCls("seller_names")}`} value={review.seller_names} onChange={(e) => setField("seller_names", e.target.value)} /></div>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1.5">Contingency deadlines (days after acceptance)</p>
                    <div className="grid grid-cols-4 gap-2">
                      <div><Label className="text-[11px] text-muted-foreground">Loan<VerifyBadge k="loan" /></Label>
                        <Input data-testid="review-loan-input" type="number" className={`mt-1 ${ringCls("loan")}`} value={review.loan} onChange={(e) => setField("loan", e.target.value)} /></div>
                      <div><Label className="text-[11px] text-muted-foreground">Appraisal<VerifyBadge k="appraisal" /></Label>
                        <Input data-testid="review-appraisal-input" type="number" className={`mt-1 ${ringCls("appraisal")}`} value={review.appraisal} onChange={(e) => setField("appraisal", e.target.value)} /></div>
                      <div><Label className="text-[11px] text-muted-foreground">Inspection<VerifyBadge k="investigation" /></Label>
                        <Input data-testid="review-investigation-input" type="number" className={`mt-1 ${ringCls("investigation")}`} value={review.investigation} onChange={(e) => setField("investigation", e.target.value)} /></div>
                      <div><Label className="text-[11px] text-muted-foreground">Close<VerifyBadge k="close_of_escrow" /></Label>
                        <Input data-testid="review-coe-input" type="number" className={`mt-1 ${ringCls("close_of_escrow")}`} value={review.close_of_escrow} onChange={(e) => setField("close_of_escrow", e.target.value)} /></div>
                    </div>
                  </div>
                  <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Buyer agent email (CC)</Label>
                    <Input data-testid="review-buyer-email-input" type="email" className="mt-1.5" value={review.buyer_agent_email} onChange={(e) => setReview({ ...review, buyer_agent_email: e.target.value })} /></div>
                  <DialogFooter>
                    <Button data-testid="review-create-button" disabled={create.isPending} onClick={createFromReview} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded gap-2">
                      {create.isPending ? "Creating…" : "Create transaction"}
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <Stat label="Active deals" value={stats?.active_deals ?? "—"} icon={Home} tone="bg-primary/10 text-primary" />
          <Stat label="Overdue tasks" value={stats?.overdue_tasks ?? "—"} icon={TriangleAlert} tone="bg-destructive/10 text-destructive" />
          <Stat label="Closing this week" value={stats?.closing_this_week ?? "—"} icon={CalendarClock} tone="bg-accent/10 text-accent" />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-2xl text-primary">Transactions</h2>
          <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="status-filter-bar">
            {["active", "all", "closed", "cancelled"].map((f) => (
              <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
                className={`px-3.5 py-1.5 text-xs uppercase tracking-widest rounded-full transition-colors ${filter === f ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-primary"}`}>
                {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
        {isLoading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : visible.length === 0 ? (
          <div className="bg-card border border-dashed border-border rounded-md p-12 text-center" data-testid="empty-state">
            <Building2 className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">{deals.length === 0 ? "No transactions yet. Create your first one to get started." : `No ${filter === "all" ? "" : filter + " "}transactions to show.`}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((d) => (
              <button key={d.id} data-testid={`deal-card-${d.id}`} onClick={() => navigate(`/deals/${d.id}`)}
                className={`group text-left bg-card border border-border rounded-md overflow-hidden hover:shadow-md hover:-translate-y-[2px] transition-all duration-300 ${d.effective_status === "cancelled" ? "opacity-70" : ""}`}>
                <div className="h-32 relative overflow-hidden">
                  <img src={d.cover_image} alt="" className="w-full h-full object-cover" />
                  {d.effective_status === "active" && d.overdue_count > 0 && (
                    <span className="absolute top-3 left-3 bg-destructive text-destructive-foreground text-xs px-2.5 py-0.5 rounded-full font-medium">{d.overdue_count} overdue</span>
                  )}
                  {d.effective_status === "closed" && (
                    <span data-testid={`closed-badge-${d.id}`} className="absolute top-3 left-3 bg-primary text-primary-foreground text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full font-semibold shadow-md">Closed{d.closed_date ? ` · ${fmtDate(d.closed_date)}` : ""}</span>
                  )}
                  {d.effective_status === "cancelled" && (
                    <span data-testid={`cancelled-badge-${d.id}`} className="absolute top-3 left-3 bg-foreground/80 text-background text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full font-semibold shadow-md">Cancelled</span>
                  )}
                  <span className="absolute top-3 right-3 bg-card/90 text-primary text-[10px] uppercase tracking-widest px-2 py-1 rounded">{d.my_role?.replace("_", " ")}</span>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="text-xl text-primary group-hover:underline underline-offset-4">{d.title}</h3>
                      <p className="text-sm text-muted-foreground">{d.address || fmtMoney(d.price)}</p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                      <span>{d.current_phase}</span><span>{d.progress}%</span>
                    </div>
                    <Progress value={d.progress} className="h-1.5" />
                  </div>
                  <div className="mt-4 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{d.completed_tasks}/{d.total_tasks} tasks</span>
                    <span className="text-primary font-medium flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {fmtDate(d.next_deadline)}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
